import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Check, ChevronDown, Plus, Settings2 } from "lucide-react";
import { getBookTypeDotClass, getBookTypeLabel } from "@/lib/book-types";
import type { Book } from "@/schemas";

interface PortfolioHeaderProps {
  books: Book[];
  selectedBookId: string;
  onBookChange: (bookId: string) => void;
  onCreatePortfolio: () => void;
  onManagePortfolios: () => void;
  loading: boolean;
}

export function PortfolioHeader({
  books,
  selectedBookId,
  onBookChange,
  onCreatePortfolio,
  onManagePortfolios,
  loading,
}: PortfolioHeaderProps) {
  const selectedBook = books.find((book) => book.book_id === selectedBookId);
  const selectedBookName =
    selectedBookId === "global"
      ? "All Portfolios"
      : (selectedBook?.name ?? "Portfolio");

  return (
    <div className="w-full sm:w-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-shrink-0">
      <div className="w-full sm:w-auto flex items-center gap-2 sm:gap-3">
        {loading ? (
          <>
            <Skeleton className="h-9 w-48" />
            <Skeleton className="h-9 w-36" />
          </>
        ) : (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="h-9 w-full sm:min-w-[220px] sm:w-auto justify-between border-border/50 bg-card/50 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {selectedBookId !== "global" && (
                      <span
                        className={`h-2 w-2 rounded-full ${getBookTypeDotClass(selectedBook?.book_type)}`}
                      />
                    )}
                    <span className="truncate">{selectedBookName}</span>
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[260px]" align="end">
                <DropdownMenuLabel>Portfolios</DropdownMenuLabel>

                <DropdownMenuItem onClick={() => onBookChange("global")}>
                  <span>All Portfolios</span>
                  {selectedBookId === "global" && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                {books.map((book) => (
                  <DropdownMenuItem
                    key={book.book_id}
                    onClick={() => onBookChange(book.book_id)}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={`h-2 w-2 rounded-full ${getBookTypeDotClass(book.book_type)}`}
                      />
                      {book.name}
                      <span className="text-xs text-muted-foreground">
                        {getBookTypeLabel(book.book_type)}
                      </span>
                    </span>
                    {selectedBookId === book.book_id && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={onCreatePortfolio}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Portfolio
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onManagePortfolios}>
                  <Settings2 className="mr-2 h-4 w-4" />
                  Manage Portfolios
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 border-border/50"
              onClick={onManagePortfolios}
              aria-label="Manage portfolios"
              title="Manage portfolios"
            >
              <Settings2 className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
